import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const EMPTY = {
  tenant_name: "", tenant_phone: "", tenant_email: "", family_members: "",
  monthly_rent: "", security_deposit: "", lease_start_date: "", lease_end_date: "",
  move_in_date: "", move_out_date: "", emergency_contact_name: "", emergency_contact_phone: "",
  id_proof_type: "Aadhaar", id_proof_number: "", rent_paid_status: "pending", status: "active",
  move_in_charge_amount: "", move_in_payment_mode: "Bank Transfer", move_in_transaction_ref: "",
  move_out_charge_amount: "", move_out_payment_mode: "Bank Transfer", move_out_transaction_ref: "",
  remarks: "",
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-slate-600">{label}</Label>
    {children}
  </div>
);

export default function TenancyDialog({ open, onOpenChange, flat, onSaved, editTenancy }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!editTenancy;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (editTenancy) {
      const normalized = {};
      Object.keys(EMPTY).forEach((k) => {
        const v = editTenancy[k];
        normalized[k] = v === null || v === undefined ? EMPTY[k] : String(v);
      });
      normalized.status = editTenancy.status || "active";
      normalized.move_in_payment_mode = editTenancy.move_in_payment_mode || "Bank Transfer";
      normalized.move_out_payment_mode = editTenancy.move_out_payment_mode || "Bank Transfer";
      setForm(normalized);
    } else {
      setForm(EMPTY);
    }
  }, [editTenancy, open]);

  const submit = async () => {
    if (!form.tenant_name) {
      toast.error("Tenant name is required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      family_members: form.family_members ? parseInt(form.family_members) : null,
      monthly_rent: form.monthly_rent ? parseFloat(form.monthly_rent) : null,
      security_deposit: form.security_deposit ? parseFloat(form.security_deposit) : null,
      lease_start_date: form.lease_start_date || null,
      lease_end_date: form.lease_end_date || null,
      move_in_date: form.move_in_date || null,
      move_out_date: form.move_out_date || null,
      move_in_charge_amount: form.move_in_charge_amount ? parseFloat(form.move_in_charge_amount) : null,
      move_out_charge_amount: form.move_out_charge_amount ? parseFloat(form.move_out_charge_amount) : null,
    };
    try {
      if (isEdit) {
        await api.put(`/tenancies/${editTenancy.id}`, payload);
        toast.success("Tenancy updated");
      } else {
        await api.post("/tenancies", { flat_id: flat.id, ...payload });
        toast.success("Tenancy record added");
      }
      setForm(EMPTY);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save tenancy");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={isEdit ? "edit-tenancy-dialog" : "add-tenancy-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEdit ? "Edit Tenancy" : "New Tenancy"} — {flat?.tower}-{flat?.flat_number}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 py-2">
          <Field label="Tenant Name"><Input value={form.tenant_name} onChange={(e) => set("tenant_name", e.target.value)} data-testid="tenant-name-input" /></Field>
          <Field label="Phone"><Input value={form.tenant_phone} onChange={(e) => set("tenant_phone", e.target.value)} data-testid="tenant-phone-input" /></Field>
          <Field label="Email"><Input value={form.tenant_email} onChange={(e) => set("tenant_email", e.target.value)} data-testid="tenant-email-input" /></Field>
          <Field label="Family Members"><Input type="number" value={form.family_members} onChange={(e) => set("family_members", e.target.value)} data-testid="tenant-family-input" /></Field>
          <Field label="Monthly Rent (₹)"><Input type="number" value={form.monthly_rent} onChange={(e) => set("monthly_rent", e.target.value)} data-testid="tenant-rent-input" /></Field>
          <Field label="Security Deposit (₹)"><Input type="number" value={form.security_deposit} onChange={(e) => set("security_deposit", e.target.value)} data-testid="tenant-deposit-input" /></Field>
          <Field label="Move-in Date"><Input type="date" value={form.move_in_date} onChange={(e) => set("move_in_date", e.target.value)} data-testid="tenant-movein-input" /></Field>
          <Field label="Move-out Date"><Input type="date" value={form.move_out_date} onChange={(e) => set("move_out_date", e.target.value)} data-testid="tenant-moveout-input" /></Field>
          <Field label="Rent Status">
            <Select value={form.rent_paid_status} onValueChange={(v) => set("rent_paid_status", v)}>
              <SelectTrigger data-testid="tenant-rentstatus-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Lease Start"><Input type="date" value={form.lease_start_date} onChange={(e) => set("lease_start_date", e.target.value)} data-testid="tenant-leasestart-input" /></Field>
          <Field label="Lease End"><Input type="date" value={form.lease_end_date} onChange={(e) => set("lease_end_date", e.target.value)} data-testid="tenant-leaseend-input" /></Field>
          <Field label="ID Proof Type">
            <Select value={form.id_proof_type} onValueChange={(v) => set("id_proof_type", v)}>
              <SelectTrigger data-testid="tenant-idtype-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Aadhaar", "PAN", "Passport", "Driving License", "Voter ID"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="ID Proof Number"><Input value={form.id_proof_number} onChange={(e) => set("id_proof_number", e.target.value)} data-testid="tenant-idnumber-input" /></Field>
          <Field label="Emergency Contact"><Input value={form.emergency_contact_name} onChange={(e) => set("emergency_contact_name", e.target.value)} data-testid="tenant-emergency-name-input" /></Field>
          <Field label="Emergency Phone"><Input value={form.emergency_contact_phone} onChange={(e) => set("emergency_contact_phone", e.target.value)} data-testid="tenant-emergency-phone-input" /></Field>
          {isEdit && (
            <Field label="Tenancy Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="tenant-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active (current)</SelectItem>
                  <SelectItem value="past">Past (moved out)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>

        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 mt-2">
          <p className="text-[11px] uppercase font-mono tracking-[0.16em] text-emerald-700 mb-3">Move-In Charges &amp; Transaction</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Field label="Charge Amount (₹)"><Input type="number" value={form.move_in_charge_amount} onChange={(e) => set("move_in_charge_amount", e.target.value)} data-testid="tenant-movein-charge-input" /></Field>
            <Field label="Payment Mode">
              <Select value={form.move_in_payment_mode} onValueChange={(v) => set("move_in_payment_mode", v)}>
                <SelectTrigger data-testid="tenant-movein-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Bank Transfer", "UPI", "Cash", "Cheque", "Card"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Transaction Ref / ID"><Input value={form.move_in_transaction_ref} onChange={(e) => set("move_in_transaction_ref", e.target.value)} placeholder="TXN123456" data-testid="tenant-movein-txn-input" /></Field>
          </div>
        </div>

        <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 mt-2">
          <p className="text-[11px] uppercase font-mono tracking-[0.16em] text-rose-700 mb-3">Move-Out Charges &amp; Transaction</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Field label="Charge Amount (₹)"><Input type="number" value={form.move_out_charge_amount} onChange={(e) => set("move_out_charge_amount", e.target.value)} data-testid="tenant-moveout-charge-input" /></Field>
            <Field label="Payment Mode">
              <Select value={form.move_out_payment_mode} onValueChange={(v) => set("move_out_payment_mode", v)}>
                <SelectTrigger data-testid="tenant-moveout-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Bank Transfer", "UPI", "Cash", "Cheque", "Card"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Transaction Ref / ID"><Input value={form.move_out_transaction_ref} onChange={(e) => set("move_out_transaction_ref", e.target.value)} placeholder="TXN123456" data-testid="tenant-moveout-txn-input" /></Field>
          </div>
        </div>

        <Field label="Remarks">
          <Textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)} rows={2} data-testid="tenant-remarks-input" />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tenancy-cancel-button">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0F2942] gap-2" data-testid="tenancy-save-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {isEdit ? "Save Changes" : "Add Tenancy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
